import React from "react";

/**
 * Root-level ErrorBoundary. Without this, any unhandled React render exception
 * (e.g. `.toFixed()` on undefined) collapses the entire shell to a black
 * screen with no context. This catches the error, logs it to the console,
 * and shows a soft recovery UI so users can reset the crashed panel or
 * reload the page.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[WarPaint] UI crashed:", error, info?.componentStack);
    // Optional: ship to Sentry / posthog / server log endpoint here in the future.
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      const msg = this.state.error?.message || String(this.state.error);
      return (
        <div
          data-testid="app-error-boundary"
          className="min-h-screen bg-black text-white flex items-center justify-center px-6"
        >
          <div className="max-w-lg w-full border-2 border-[#FF3B30] bg-[#0a0a0a] p-8">
            <div className="mono text-[10px] uppercase tracking-[0.28em] text-[#FF3B30] mb-2">
              Client Error · WarPaint DFS
            </div>
            <div className="heading text-2xl font-black uppercase tracking-tight mb-3">
              Something crashed on this page.
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed mb-4">
              A UI component threw an unhandled exception. Your lineups, saved config, and slate
              data are safe on the server — this only affects the current view. Try resetting the
              panel; if it keeps happening, reload the page.
            </p>
            <pre
              data-testid="app-error-message"
              className="mono text-[11px] text-zinc-500 bg-black border border-zinc-800 px-3 py-2 mb-5 overflow-x-auto"
            >
              {msg}
            </pre>
            <div className="flex gap-2">
              <button
                data-testid="app-error-retry"
                onClick={this.reset}
                className="bg-[#39FF14] hover:bg-[#2ee30d] text-black font-black uppercase tracking-widest text-xs py-2.5 px-4 transition-colors"
              >
                Reset panel
              </button>
              <button
                data-testid="app-error-reload"
                onClick={() => window.location.reload()}
                className="border border-zinc-800 hover:border-zinc-600 text-zinc-300 uppercase tracking-widest text-xs py-2.5 px-4 transition-colors mono"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
